import { Observable } from 'rxjs'
import { Style } from '../models/style';
import { Injectable } from '@angular/core';
import { AppService } from './app.service';
import { HttpClient } from '@angular/common/http';
import { ProductService } from './product.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ReferalProgramModalContent } from '../modal/referral-program.modal';
import { MESSAGE_TYPE, ShareWithFriendsService } from './share-with-friends.service';
import { ViewTablePage, ViewTablePagedData, ViewTableService } from '../modules/data-view-table/view-table.service';
import { BrowserUtil } from '../utils/browser-util';
import { Logger } from '../utils/log-util';
import { GTMService } from './gtm.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  collection: any;
  baseUrl: string;

  constructor(
    private http: HttpClient,
    private deviceDetectorService: DeviceDetectorService,
    private viewTableService: ViewTableService,
    private appService: AppService,
    private productService: ProductService,
    private deviceService: DeviceDetectorService,
    private modalService: NgbModal,
    private shareWithFriendsService: ShareWithFriendsService,
    private gtmService: GTMService,
  ) {
    this.baseUrl = `${this.appService.getAppBaseUrl()}/user`;
  }

  markLoggedIn() {
    return this.http.post(`${this.baseUrl}/mark-logged-in`, {
      device_meta: this.deviceDetectorService.getDeviceInfo()
    });
  }

  updateProfile(user: any) {
    return this.http.patch(`${this.baseUrl}/profile`, user)
  }

  updateOnboardingQuizAnswers(quizAnswers: any, brands: any[] = [], deleted_brands: any[] = [], user_meta = {}): Observable<any> {
    return this.http.post(`${this.baseUrl}/onboarding-quiz`, { answers: quizAnswers, brands: brands, deleted_brands: deleted_brands, ...user_meta })
  }

  answerInboundRequestQuizQuiz(quizAnswers: any, requestId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/inbound-request-quiz`, { answers: quizAnswers, "request_id": requestId })
  }

  compeletePaymentToProceed(requestId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/inbound-request-payment`, { request_id: requestId })
  }

  updateStylePreferences(styles: Partial<Style>[], deleted_styles: Partial<Style>[] = []): Observable<any> {
    return this.http.post(`${this.baseUrl}/styles`, { styles: styles, deleted_styles: deleted_styles })
  }

  createQatchUserWithPhone(userDetails: any, queryParams = {}): Observable<any> {
    return this.http.post(`${this.baseUrl}`, userDetails, { params: queryParams })
  }

  createUserWithConvo(userDetails: any, queryParams = {}): Observable<any> {
    return this.http.post(`${this.baseUrl}/signup`, userDetails, { params: queryParams })
  }

  createUserFrom12days(userDetails: any, queryParams = {}): Observable<any> {
    return this.http.post(`${this.baseUrl}/promo-events-optin`, userDetails, { params: queryParams })
  }

  updateQatchUser(userDetails: any): Observable<any> {
    return this.http.patch(`${this.baseUrl}`, userDetails)
  }

  checkIfQatchUserExists(userIdentifier: string): Observable<any> {
    return this.http.head(`${this.baseUrl}/${userIdentifier}`)
  }

  createFirebaseUser(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/firebase`, data)
  }

  updatePassword(uri: string, data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/${uri}`, data)
  }

  raiseSupportRequest(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/grievances`, data)
  }

  raiseContactUsRequest(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl.replace('/user', '')}/grievances`, data)
  }

  getMyQatches(page: Partial<ViewTablePage>, sort: any, filters: any): Observable<ViewTablePagedData> {
    return this.viewTableService.getData(`${this.baseUrl}/qatches`, page, sort, filters, 'qatches');
  }

  getWishlist(userId: string, page: Partial<ViewTablePage>, sort: any, filters: any): Observable<ViewTablePagedData> {
    return this.viewTableService.getData(`${this.appService.getAppBaseUrl()}/user/${userId}/wishlist`, page, sort, filters, 'wishlist');
  }

  getFilters(filters: any, params: any): Observable<any> {
    return this.http.post(`${this.appService.getAppBaseUrl()}/filters`, { filters }, { params });
  }

  generateReferralLink(): Observable<any> {
    return this.http.get(`${this.baseUrl}/referral-link`);
  }

  addToMyQatches(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/qatches`, data);
  }

  addOrUpdateReaction(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/reaction`, data);
  }

  addToMyQatchWithReaction(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/qatch-with-reaction`, data);
  }

  private buildClickPayload(data: any) {
    return {
      device_meta: this.deviceDetectorService.getDeviceInfo(),
      text_id: data.text_id,
      product_id: data.product_id,
      collection_id: this.collection?.collection_id || null
    };
  }

  private navigateAfterLoggingClick(data: any, target: Window, fallbackUrl: string) {
    this.http.post(`${this.baseUrl}/log-product-click`, this.buildClickPayload(data)).subscribe({
      next: (res: any) => {
        // Push GTM event with server response affiliate link
        this.pushProductClickToGTM(data, res.affiliate_link, 'server');
        target.location.href = res.affiliate_link;
      },
      error: () => {
        // Push GTM event with fallback affiliate link
        this.pushProductClickToGTM(data, fallbackUrl, 'fallback');
        target.location.href = fallbackUrl;
      }
    });
  }

  private pushProductClickToGTM(data: any, affiliateLink: string, source: 'server' | 'fallback') {
    try {
      this.gtmService.push({
        event: 'product_click',

        product_id: data.product_id,
        product_price: data.discount_price || data.price,
        product_brand: data.brand_name,
        affiliate_link: affiliateLink,
        affiliate_link_source: source,

        text_id: data.text_id,
        collection_id: data.collection_id ?? this.collection?.collection_id,
    
        page_url: window.location.href,
        page_path: window.location.pathname,
        page_title: document.title
      });
    } catch (err) {
      Logger.error("Error pushing GTM event for product click", err);
    }
  }

  logUserClick(data: any) {
    const defaultUrlWithTrackingParam = this.affiliateLinkWithTrackingParam(data);
    try{
      if (BrowserUtil.isInAppBrowser()) {
        // No popup needed, navigate current tab post-response
        this.navigateAfterLoggingClick(data, window, defaultUrlWithTrackingParam);
        return;
      }
      // Regular browsers: open blank tab synchronously, redirect it post-response
      const newWindow = window.open('about:blank', '_blank');
      if (newWindow) {
        this.navigateAfterLoggingClick(data, newWindow, defaultUrlWithTrackingParam);
      } else {
        // Push GTM event before navigation for immediate redirect scenario
        this.pushProductClickToGTM(data, defaultUrlWithTrackingParam, 'fallback');
        window.location.href = defaultUrlWithTrackingParam;
      }
    }catch(err){
      Logger.error("Error occurred while opening the url");
      // Push GTM event even in error scenario
      this.pushProductClickToGTM(data, defaultUrlWithTrackingParam, 'fallback');
      window.location.href = defaultUrlWithTrackingParam;
    }
  }

  affiliateLinkWithTrackingParam(data: any) {
    if (data.sub_id_parameter && data.text_id) {
      const url = new URL(data.affiliate_link);
      url.searchParams.append(data.sub_id_parameter, data.text_id);
      return url.toString();
    }
    return data.affiliate_link;
  }

  lookupUser(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/lookup`, data);
  }

  lookupByUsername(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/search`, data);
  }

  referAProduct(product: any) {
    this.productService.referAProduct(product);
  }

  referAFriend(referral_link: string) {
    const text = `Hey, join Qatch like I did to get cute outfits via text! Trust me, you’re gonna love it.`;
    if (this.deviceService.isDesktop()) {
      const modalRef = this.modalService.open(ReferalProgramModalContent, { centered: true });
      modalRef.componentInstance.referral_link = referral_link;
      modalRef.componentInstance.text = text;
      modalRef.result.then((result) => {
      }, (reason) => { });
    } else {
      this.shareWithFriendsService.share(referral_link, MESSAGE_TYPE.REFER_A_FRIEND);
    }
  }

  getUserConvos(): Observable<{ convos: any }> {
    return this.http.get<{ convos: any }>(`${this.baseUrl}/convos`);
  }

  updateUserConvos(convos) {
    return this.http.put(`${this.baseUrl}/convos`, { convos });
  }
}
